import React, { useState } from 'react';
import { Typography, Skeleton, Stack, Box, Button } from 'common/components/material';
import useParamsProfileId from 'common/hooks/useParamsProfileId';
import useTheme from '@mui/material/styles/useTheme';
import useIntersectionObserver from 'features/org-root/hooks/useIntersectionObserver';
import {
  SkillContributionsContainer,
  BaseWorkItem,
} from 'features/matching-engine/components/ManageSkillDashboardDrawer';
import { useGetWorkItems } from 'features/matching-engine/hooks/useGetWorkItems';
import WarningIcon from '@mui/icons-material/Warning';
import colorPalette from 'common/theme/colorPalette';
import { getGroupedWorkItems } from 'features/matching-engine/components/MatchingEngineSearchView';

const ITEMS_PER_CLICK = 3;

type Contribution = {
  id: string;
  date: Date;
  totalContributedDays: number;
  summary: string;
};

export type WorkItem = {
  id: string;
  source: string;
  version?: string;
  components?: string;
  date: Date;
  totalContributedDays: number;
  name: string;
  criticality: string;
  sponsor?: string;
  contributions: Contribution[];
};

const EndorsedSkillTrackRecord: React.FC<{
  profileId?: string;
  skillId?: string[];
}> = ({ profileId, skillId }) => {
  const { palette } = useTheme();
  const { profileId: profileId2 } = useParamsProfileId();
  const { setNode } = useIntersectionObserver();
  profileId = profileId ?? profileId2;
  const { data = [], isLoading } = useGetWorkItems({
    profileId,
    skillIds: skillId,
  });

  const groupedItems = React.useMemo(() => getGroupedWorkItems(data), [data]);

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_CLICK);

  const itemsToShow = groupedItems.slice(0, visibleCount) ?? [];

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_CLICK);
  };

  if (isLoading || !groupedItems) {
    return (
      <Skeleton
        ref={setNode}
        variant="rectangular"
        sx={{ borderRadius: '0.75rem', mr: 2, mb: 3 }}
        height="6rem"
      />
    );
  }

  return (
    <Typography variant="body3" color={palette.Text.Headline}>
      {groupedItems?.length === 0 && (
        <Typography variant="body4" sx={{ fontStyle: 'italic' }}>
          Information not found
        </Typography>
      )}

      {itemsToShow.map(({ groupHeader, items }) => (
        <Box key={groupHeader.id} sx={{ mb: 3 }}>
          <Stack spacing={1} px={2}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography component="span" fontSize="0.8rem" fontWeight={600}>
                {groupHeader.name}
              </Typography>
              {groupHeader.criticality === 'High' && (
                <WarningIcon sx={{ color: colorPalette.YELLOW400, fontSize: 16 }} />
              )}
            </Stack>
            {items.map((item: BaseWorkItem, idx: any) => (
              <SkillContributionsContainer
                key={`${item.id}-${idx}`}
                profileId={profileId}
                workItem={item}
              />
            ))}
          </Stack>
        </Box>
      ))}

      {visibleCount < (data?.length ?? 0) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button size="small" onClick={handleShowMore}>
            Show More
          </Button>
        </Box>
      )}
    </Typography>
  );
};

export default EndorsedSkillTrackRecord;
